import axios from "axios";
import { defineStore } from "pinia";
import { markRaw } from "vue";
import { MiaPlaza } from "@/Reinforced.Typings";
import QueueStore from "./QueueStore";

export type DataProvider<T> = (offset: number, limit: number) => Promise<MiaPlaza.Control.Vue.Stores.Shared.IRangeWithOffset<T> | T[]>;

type QueueKey = { startOffset: number, endOffset: number };

export function createListStore<T>(id: string, dataProvider: DataProvider<T>, initialItems: T[], totalCountEstimate: number, unfilteredOffset: number, reset: boolean) {
	let created = false;

	const store = defineStore(id, {
		state: () => {
			created = true;
			return {
				data: initialItems,
				dataProvider,
				dataTotalCountEstimate: totalCountEstimate,
				internal: markRaw({
					// The offset in the backing multitude that corresponds to the
					// first item that is not in data yet, i.e., the offset of the
					// item at data.length.
					// See IMultitude.GetRange() for details.
					unfilteredOffset,
					queue: new QueueStore<QueueKey>(),
				}),
			};
		},
		getters: {
			items: (state) => {
				return state.data;
			},
			totalCountEstimate: (state) => {
				return state.dataTotalCountEstimate;
			},
			isResetting: (state) => {
				return Number(state.data.length === 0 && state.dataTotalCountEstimate === 1);
			},
		},
		actions: {
			async reset(dataProvider?: string | T[] | DataProvider<T>) {
				this.internal.queue.markInvalid();
				if (dataProvider != null) {
					if (typeof (dataProvider) === "string" || Array.isArray(dataProvider)) {
						dataProvider = makeDataProvider<T>(dataProvider);
					}
					this.dataProvider = dataProvider;
				}
				this.internal.unfilteredOffset = 0;
				this.doReset();
			},
			doReset(): void {
				this.data = [];
				this.dataTotalCountEstimate = 1;
			},
			async load(keys: number[]) {
				if (keys == null || keys.length === 0) {
					return;
				}
				const startOffset = Math.min(...keys);
				const endOffset = Math.max(...keys) + 1;
				this.internal.queue.enqueue({ startOffset, endOffset });
				await this.internal.queue.dequeue(this.doLoad);
			},
			async doLoad(keys: QueueKey[], isValid: () => boolean): Promise<void> {
				const startOffset = this.data.length;
				const endOffset = Math.max(...keys.map((key) => key.endOffset));

				if (startOffset >= endOffset) {
					return;
				}

				if (this.dataTotalCountEstimate <= this.data.length) {
					// By convention with the server:
					// When the number of items match the count estimate, then there is nothing to load from the server anymore.
					return;
				}

				const offset = this.internal.unfilteredOffset;
				const limit = endOffset - startOffset;

				let response = await this.dataProvider(offset, limit);

				function wrapArrayResponse<T>(response: T[], offset: number, limit: number) {
					const exhausted = response.length < limit;
					return {
						Items: response,
						TotalCountEstimate: offset + response.length + (exhausted ? 0 : 1),
						UnfilteredOffset: offset,
						UnfilteredLimit: limit,
					};
				}

				if (Array.isArray(response)) {
					response = wrapArrayResponse(response, offset, limit);
				}

				if (!isValid()) {
					// If reset has been called since we started this request, we ignore this obsolete data.
					return;
				}

				this.insert(response.Items);
				this.estimateLength(response.TotalCountEstimate);
				this.internal.unfilteredOffset = response.UnfilteredOffset + response.UnfilteredLimit;
			},
			insert(items: T[]): void {
				for (const item of items) {
					this.data.push(item);
				}
			},
			estimateLength(totalCountEstimate: number): void {
				this.dataTotalCountEstimate = totalCountEstimate;
			},
		},
	})();

	if (!created && reset) {
		store.reset(dataProvider);
	}

	return store;
}

export type IListStoreDefinition<T> = ReturnType<typeof createListStore<T>>;
export type IListStore<T> = ReturnType<IListStoreDefinition<T>>;

export function useListStore<T>(id: string): IListStore<T> {
	return defineStore(id, {
		state: () => { throw Error(`store ${id} must be created with createListStore() before it can be used`); },
	})();
}

export function makeDataProvider<T>(dataUrl: string | T[]): DataProvider<T> {
	return async (offset: number, limit: number) => {
		if (typeof dataUrl === "string") {
			const response = await axios.get<MiaPlaza.Control.Vue.Stores.Shared.IRangeWithOffset<T>>(dataUrl
				.replace(MiaPlaza.Control.Vue.Stores.Shared.AjaxHelpers.PLACEHOLDER_OFFSET, offset.toString())
				.replace(MiaPlaza.Control.Vue.Stores.Shared.AjaxHelpers.PLACEHOLDER_LIMIT, limit.toString()));
			return response.data;
		}
		else {
			return Promise.resolve({
				Items: dataUrl.slice(offset, offset + limit),
				UnfilteredOffset: offset,
				UnfilteredLimit: limit,
				TotalCountEstimate: dataUrl.length,
			});
		}
	};
}
